import React from 'react';

import { useGraphQLMutation } from '@betterment/graphql';
import { Box, Button, H1, HStack, Text } from '@betterment/samba-react';
import { BettermentLogo, TakeoverHeader } from '@betterment/style-closet-react';
import { useLoaderData } from 'react-router-dom';

import { AcceptAdvisoryRelationshipApprovalRequestDocument } from './graphql/mutation.acceptAdvisorApproval.generated';

import AdvisoryAgreements from '../../Agreements/AdvisoryAgreements';
import PricingPlan from '../../Agreements/PricingPlan';
import TermsAndConditions from '../../Agreements/TermsAndConditions';
import GenericErrorPage from '../../error/GenericErrorPage';
import { AdvisorFee, ClientAgreements } from '../../FirmTermsAcceptance/data';
import { ClientEnvelopeApprovalRequestSource } from '../../graphql/schema';
import Pencils from '../../images/pencils.png';
import { IdempotencyProviderWithFeatureRegistry } from '../../RetailApp/graphql/idempotency/IdempotencyProviderWithFeatureRegistry';
import { locationAssign } from '../../Shared/path-helpers';
import { urlBuilder } from '../../Shared/urlBuilder';

export interface AgreementsData {
  approvalRequestId: string;
  firmLogoUrl: string | undefined;
  firmName: string;
  advisorFee: AdvisorFee;
  platformFee: string;
  agreements: ClientAgreements;
  clientEnvelopeApprovalRequestSource: ClientEnvelopeApprovalRequestSource;
}

const AgreementsAppContent: React.FC = () => {
  const data = useLoaderData<AgreementsData>();
  const {
    approvalRequestId,
    firmLogoUrl,
    firmName,
    advisorFee,
    platformFee,
    agreements,
    clientEnvelopeApprovalRequestSource,
  } = data;

  const [acceptApproval, { loading, error }] = useGraphQLMutation(AcceptAdvisoryRelationshipApprovalRequestDocument);
  const [hasError, setHasError] = React.useState(false);

  const redirectToHome = () => {
    locationAssign(urlBuilder.appHome.absolute());
  };

  const handleConfirm = async () => {
    try {
      await acceptApproval({
        variables: {
          input: {
            approvalRequestId,
            clientEnvelopeApprovalRequestSource,
            clientAgreementToken: agreements.clientAgreement?.token,
            advPart2bAgreementToken: agreements.advPart2bAgreement?.token,
            advisorNetworkAgreementToken: agreements.banAgreement?.token,
          },
        },
      });

      redirectToHome();
    } catch (err) {
      setHasError(true);
    }
  };

  if (error) throw error;

  if (hasError) {
    return <GenericErrorPage />;
  }

  return (
    <HStack
      css={{
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        height: '100vh',
      }}
    >
      <Box
        css={{
          display: { base: 'none', lg: 'block' },
          height: '100vh',
          overflow: 'hidden',
          '& img': {
            height: '100%',
            width: '100%',
            objectFit: 'cover',
            display: 'block',
          },
        }}
      >
        <img src={Pencils} alt="Pencils on notebook" />
      </Box>
      <Box
        css={{
          width: { base: '100%', lg: '55%' },
          height: '100vh',
          overflowY: 'auto',
          pb: 'space.800',
          px: { base: 'space.300', lg: 'space.600' },
        }}
      >
        <Box css={{ position: 'relative' }}>
          <TakeoverHeader
            title="New client agreements"
            closeHandler={redirectToHome}
            logo={firmLogoUrl ? <img src={firmLogoUrl} alt="Firm logo" /> : <BettermentLogo />}
          />
        </Box>
        <Box>
          <H1
            css={{
              mt: 'space.400',
              mb: 'space.200',
              fontSize: 'fontSize.600',
              fontWeight: 'fontWeights.bold',
            }}
          >
            Make {firmName} your advisor
          </H1>
          <Box
            css={{
              width: { base: '90%', lg: '70%' },
            }}
          >
            <Text
              as="p"
              css={{
                marginBottom: 'space.300',
              }}
            >
              Below is some important information regarding {firmName}&apos;s financial services. Your advisory
              agreement with {firmName} is an agreement solely between you and your advisor, and Betterment and its
              affiliates are not by any means acquiring any obligation or legal duties by permitting it to be executed
              here. Declining to make {firmName} your advisor will have no impact to your existing Betterment account.
            </Text>

            <PricingPlan advisorFee={advisorFee} firmName={firmName} platformFee={platformFee} />

            <TermsAndConditions />

            <AdvisoryAgreements firmName={firmName} agreements={agreements} />

            <Text
              as="p"
              css={{
                marginBottom: 'space.400',
              }}
            >
              By clicking confirm, I acknowledge that I have read and agree to the terms above.
            </Text>
          </Box>
        </Box>
        <Button type="button" onClick={handleConfirm} disabled={loading}>
          {loading ? 'Confirming...' : 'Confirm'}
        </Button>
      </Box>
    </HStack>
  );
};

const AgreementsApp: React.FC = () => {
  return (
    <IdempotencyProviderWithFeatureRegistry featureName="clientEnvelopeApprovals">
      <AgreementsAppContent />
    </IdempotencyProviderWithFeatureRegistry>
  );
};

export default AgreementsApp;
